import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertTriangle } from 'lucide-react';
import type { CreditMemoDeletePreview } from '@/lib/api';

interface DeleteCreditMemoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creditMemoNumber: string;
  preview: CreditMemoDeletePreview | null;
  onConfirm: () => Promise<void>;
  isDeleting: boolean;
}

export function DeleteCreditMemoModal({
  open,
  onOpenChange,
  creditMemoNumber,
  preview,
  onConfirm,
  isDeleting,
}: DeleteCreditMemoModalProps) {
  const hasAllocations = preview?.has_allocations ?? false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <DialogTitle>Delete Credit Memo #{creditMemoNumber}?</DialogTitle>
          </div>
        </DialogHeader>

        {hasAllocations ? (
          <div className="space-y-4">
            <DialogDescription>
              This credit memo has <strong>${preview?.total_allocated}</strong> applied to documents.
              Deleting it will undo these payments:
            </DialogDescription>

            {preview?.affected_invoices && preview.affected_invoices.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Affected Invoices</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2">Invoice</th>
                        <th className="text-right p-2">Amount</th>
                        <th className="text-left p-2">Status Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.affected_invoices.map((inv) => (
                        <tr key={inv.id} className="border-t">
                          <td className="p-2">#{inv.invoice_number}</td>
                          <td className="p-2 text-right">${inv.allocated_amount}</td>
                          <td className="p-2">
                            <span className="text-muted-foreground">{inv.current_status.replace(/_/g, ' ')}</span>
                            {inv.current_status !== inv.will_revert_to && (
                              <span> → <span className="text-amber-600">{inv.will_revert_to.replace(/_/g, ' ')}</span></span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {preview?.affected_quotes && preview.affected_quotes.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Affected Quotes</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2">Quote</th>
                        <th className="text-right p-2">Amount</th>
                        <th className="text-left p-2">Status Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.affected_quotes.map((q) => (
                        <tr key={q.id} className="border-t">
                          <td className="p-2">#{q.quote_number}</td>
                          <td className="p-2 text-right">${q.allocated_amount}</td>
                          <td className="p-2">
                            <span className="text-muted-foreground">{q.current_status.replace(/_/g, ' ')}</span>
                            {q.current_status !== q.will_revert_to && (
                              <span> → <span className="text-amber-600">{q.will_revert_to.replace(/_/g, ' ')}</span></span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : (
          <DialogDescription>
            This action cannot be undone. The credit memo will be permanently deleted.
          </DialogDescription>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
